import { useState, useEffect } from 'react';
import {
    Box, Heading, Flex, Button, HStack, Text, Badge, Input, InputGroup, InputLeftElement,
    Table, Thead, Tbody, Tr, Th, Td, TableContainer, useToast, Spinner, Center, Select,
    Card, CardBody,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, useDisclosure,
} from '@chakra-ui/react';
import { FiSearch, FiRotateCcw, FiUsers } from 'react-icons/fi';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { getGradeLabelOverride } from '../utils/gradeLabels';

const OldStudentsPage = () => {
    const toast = useToast();
    // [A2] 年級顯示依登入者機構 grade_labels 渲染；沒設定（天籟現況）時 fallback 回現行文字
    const { user } = useAuth();
    const gradeLabels = user?.institution?.grade_labels || null;
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [classes, setClasses] = useState([]);

    // 復學
    const restoreDisclosure = useDisclosure();
    const [restoreTarget, setRestoreTarget] = useState(null);
    const [restoreClassId, setRestoreClassId] = useState('');
    const [restoreLoading, setRestoreLoading] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await authAPI.getArchivedStudents(search.trim() || undefined);
            setItems(Array.isArray(res.data) ? res.data : []);
        } catch (e) {
            toast({ title: '載入失敗', status: 'error', duration: 3000 });
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const t = setTimeout(fetchData, 400); // 搜尋 debounce
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    useEffect(() => {
        authAPI.getClassesLite().then(res => setClasses(res.data || [])).catch(() => {});
    }, []);

    const openRestore = (stu) => {
        setRestoreTarget(stu);
        setRestoreClassId('');
        restoreDisclosure.onOpen();
    };

    const doRestore = async () => {
        if (!restoreClassId) return;
        setRestoreLoading(true);
        try {
            await authAPI.restoreStudent(restoreTarget.id, parseInt(restoreClassId, 10));
            toast({ title: '復學成功', description: `${restoreTarget.full_name} 已恢復在籍`, status: 'success', duration: 3000 });
            restoreDisclosure.onClose();
            fetchData();
        } catch (e) {
            toast({ title: '復學失敗', description: e.response?.data?.detail || '', status: 'error', duration: 3000 });
        } finally {
            setRestoreLoading(false);
        }
    };

    const parentSummary = (parents = []) =>
        (parents || []).map(p => `${p.full_name || '家長'}${p.phone_number ? ` (${p.phone_number})` : ''}`).join('、') || '—';

    return (
        <Box p={{ base: 4, md: 6 }}>
            <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={3}>
                <HStack>
                    <FiUsers />
                    <Heading size="lg">舊生查詢</Heading>
                    <Badge colorScheme="gray">已離校</Badge>
                </HStack>
                <InputGroup w={{ base: '100%', md: '260px' }}>
                    <InputLeftElement pointerEvents="none"><FiSearch color="gray" /></InputLeftElement>
                    <Input placeholder="依姓名搜尋…" value={search} onChange={(e) => setSearch(e.target.value)} />
                </InputGroup>
            </Flex>

            <Text fontSize="sm" color="gray.500" mb={3}>
                已離校（舊生）名單，跨班級、不參與日常運作；資料保留，可查詢或「復學」還原。
            </Text>

            {loading ? (
                <Center py={16}><Spinner /></Center>
            ) : items.length === 0 ? (
                <Center py={16}><Text color="gray.500">目前沒有舊生資料</Text></Center>
            ) : (
                // 表格要有卡片承載：本頁原本讓 Tbody 直接透出行政外殼（neutral.50），
                // 而 Thead 的 gray.50 又剛好是同一個色，換配色後整張表跟背景糊在一起。
                // 其餘清單頁（班級/機構/學生/職員/公告/每日出勤）都是 Card>CardBody>TableContainer，
                // 這裡是唯一的例外，改成跟它們一致。
                // Card 帶 overflow="hidden" 由卡片自己的圓角去切表格（TableContainer 不再另外設圓角，
                // 否則兩層圓角不同會在四角露出縫），與 ClassesPage / InstitutionsPage / StudentsPage 一致。
                <Card w="100%" overflow="hidden">
                    <CardBody p={0}>
                        <TableContainer>
                            <Table size="sm">
                                <Thead bg="gray.50">
                                    <Tr>
                                        <Th>姓名</Th>
                                        <Th>年級</Th>
                                        <Th>家長</Th>
                                        <Th>離校日期</Th>
                                        <Th textAlign="right">操作</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {items.map(s => (
                                        <Tr key={s.id}>
                                            <Td fontWeight="medium">{s.full_name}</Td>
                                            <Td>{s.grade ? (getGradeLabelOverride(s.grade, gradeLabels) || `${s.grade} 年級`) : '—'}</Td>
                                            <Td maxW="260px" whiteSpace="normal">{parentSummary(s.parents)}</Td>
                                            <Td>{s.left_at || '—'}</Td>
                                            <Td textAlign="right">
                                                <Button size="xs" leftIcon={<FiRotateCcw />} colorScheme="green" variant="outline" onClick={() => openRestore(s)}>
                                                    復學
                                                </Button>
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </TableContainer>
                    </CardBody>
                </Card>
            )}

            {/* 復學 Modal */}
            <Modal isOpen={restoreDisclosure.isOpen} onClose={restoreDisclosure.onClose} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>復學：{restoreTarget?.full_name}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text mb={2} fontSize="sm" color="gray.600">選擇要掛回的班級：</Text>
                        <Select placeholder="請選擇班級" value={restoreClassId} onChange={(e) => setRestoreClassId(e.target.value)}>
                            {classes.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </Select>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={restoreDisclosure.onClose}>取消</Button>
                        <Button colorScheme="green" isDisabled={!restoreClassId} isLoading={restoreLoading} onClick={doRestore}>
                            確定復學
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default OldStudentsPage;
